import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const RentalNotificationModal = ({ 
  notification, 
  isOpen, 
  onClose, 
  onApprove, 
  onReject, 
  onViewDetails,
  onMarkAsRead,
  language = 'pt-BR' 
}) => {
  if (!notification) return null;

  const { tipo, dados_extras } = notification;
  const truck = dados_extras?.truck || {};
  const rental = dados_extras?.rental || {};

  // Modal para Admin - Nova Solicitação
  if (tipo === 'rental_request') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-3xl">🚛</span>
              {language === 'pt-BR' ? 'Nova Solicitação de Aluguel' : 'New Rental Request'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-bold text-blue-900 mb-2">
                {language === 'pt-BR' ? '🚛 Caminhão Solicitado' : '🚛 Requested Truck'}
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">{language === 'pt-BR' ? 'Modelo:' : 'Model:'}</span>
                  <p className="font-semibold">{truck.modelo}</p>
                </div>
                <div>
                  <span className="text-gray-600">{language === 'pt-BR' ? 'Placa:' : 'Plate:'}</span>
                  <p className="font-semibold">{truck.placa}</p>
                </div>
                {truck.vin_number && (
                  <div className="col-span-2">
                    <span className="text-gray-600">🔖 VIN:</span>
                    <p className="font-semibold font-mono text-xs">{truck.vin_number}</p>
                  </div>
                )}
                <div>
                  <span className="text-gray-600">KM:</span>
                  <p className="font-semibold">{truck.quilometragem_atual?.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-600">{language === 'pt-BR' ? 'Cor:' : 'Color:'}</span>
                  <p className="font-semibold">{truck.cor}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">
                {language === 'pt-BR' ? '👤 Solicitante' : '👤 Requester'}
              </h3>
              <p className="font-semibold">{rental.pm_name}</p>
              <p className="text-sm text-gray-600">@{rental.pm_username}</p>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-bold text-green-900 mb-2">
                {language === 'pt-BR' ? '📅 Período' : '📅 Period'}
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">{language === 'pt-BR' ? 'Início:' : 'Start:'}</span>
                  <p className="font-semibold">{rental.data_inicio_solicitada}</p>
                </div>
                <div>
                  <span className="text-gray-600">{language === 'pt-BR' ? 'Fim:' : 'End:'}</span>
                  <p className="font-semibold">{rental.data_fim_solicitada || (language === 'pt-BR' ? 'Em aberto' : 'Open')}</p>
                </div>
              </div>
            </div>

            {rental.proposito && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="font-bold text-yellow-900 mb-2">
                  {language === 'pt-BR' ? '📋 Propósito' : '📋 Purpose'}
                </h3>
                <p className="text-sm">{rental.proposito}</p>
              </div>
            )}

            <div className="bg-purple-50 p-3 rounded-lg text-sm">
              <p className="text-purple-900">
                💰 <strong>{language === 'pt-BR' ? 'Valor:' : 'Rate:'}</strong> $100/dia
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose}>
              {language === 'pt-BR' ? 'Ver Depois' : 'View Later'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => onReject(notification)}
            >
              ❌ {language === 'pt-BR' ? 'Rejeitar' : 'Reject'}
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => onApprove(notification)}
            >
              ✅ {language === 'pt-BR' ? 'Aprovar' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Modal para PM - Aluguel Aprovado
  if (tipo === 'rental_approved') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <span className="text-3xl">✅</span>
              {language === 'pt-BR' ? 'Aluguel Aprovado!' : 'Rental Approved!'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
              <p className="text-2xl mb-2">🎉</p>
              <p className="font-semibold text-green-900">
                {language === 'pt-BR' 
                  ? 'Sua solicitação foi aprovada!' 
                  : 'Your request has been approved!'}
              </p>
            </div>

            <div className="bg-white p-4 rounded-lg border">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{language === 'pt-BR' ? 'Caminhão:' : 'Truck:'}</span>
                  <span className="font-semibold">{truck.modelo} ({truck.placa})</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{language === 'pt-BR' ? 'Início:' : 'Start:'}</span>
                  <span className="font-semibold">{rental.data_inicio_solicitada}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{language === 'pt-BR' ? 'Valor:' : 'Rate:'}</span>
                  <span className="font-semibold">$100/dia</span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-bold text-blue-900 mb-2">
                {language === 'pt-BR' ? '📋 Próximos Passos:' : '📋 Next Steps:'}
              </h3>
              <ol className="text-sm space-y-1 list-decimal list-inside">
                <li>{language === 'pt-BR' ? 'Faça check-in no dia da retirada' : 'Check-in on pickup day'}</li>
                <li>{language === 'pt-BR' ? 'Assine digitalmente o termo' : 'Sign digitally'}</li>
                <li>{language === 'pt-BR' ? 'Registre KM e combustível' : 'Register KM and fuel'}</li>
              </ol>
            </div>
          </div>

          <DialogFooter>
            <Button 
              onClick={() => {
                if (onMarkAsRead) onMarkAsRead(notification);
                onClose();
              }} 
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {language === 'pt-BR' ? 'Entendi!' : 'Got it!'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Modal para PM - Aluguel Rejeitado
  if (tipo === 'rental_rejected') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <span className="text-3xl">❌</span>
              {language === 'pt-BR' ? 'Solicitação Rejeitada' : 'Request Rejected'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-red-900">
                {language === 'pt-BR' 
                  ? 'Sua solicitação foi rejeitada pelo administrador.' 
                  : 'Your request was rejected by administrator.'}
              </p>
            </div>

            <div className="bg-white p-4 rounded-lg border">
              <p className="text-sm font-semibold mb-1">
                {language === 'pt-BR' ? 'Caminhão:' : 'Truck:'}
              </p>
              <p className="text-sm">{truck.modelo} ({truck.placa})</p>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-sm font-semibold mb-1 text-yellow-900">
                {language === 'pt-BR' ? 'Motivo:' : 'Reason:'}
              </p>
              <p className="text-sm text-yellow-800">{dados_extras.motivo}</p>
            </div>
          </div>

          <DialogFooter>
            <Button 
              onClick={() => {
                if (onMarkAsRead) onMarkAsRead(notification);
                onClose();
              }} 
              className="w-full"
            >
              {language === 'pt-BR' ? 'Fechar' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Modal para Admin - Checkout Solicitado
  if (tipo === 'checkout_requested') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-3xl">🏁</span>
              {language === 'pt-BR' ? 'Checkout Solicitado' : 'Checkout Requested'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="font-semibold text-blue-900 mb-2">
                {rental.pm_name} {language === 'pt-BR' ? 'solicitou checkout' : 'requested checkout'}
              </p>
              <p className="text-sm text-blue-800">
                {truck.modelo} ({truck.placa})
              </p>
            </div>

            <div className="bg-white p-4 rounded-lg border space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Check-in:</span>
                <span className="font-semibold">{rental.check_in?.data}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">KM Inicial:</span>
                <span className="font-semibold">{rental.check_in?.km_inicial}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{language === 'pt-BR' ? 'Combustível:' : 'Fuel:'}</span>
                <span className="font-semibold">{rental.check_in?.combustivel_inicial}</span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose}>
              {language === 'pt-BR' ? 'Ver Depois' : 'View Later'}
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => onViewDetails(notification)}
            >
              {language === 'pt-BR' ? '🏁 Fazer Checkout' : '🏁 Do Checkout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
};

export default RentalNotificationModal;